import { useCallback, useEffect, useRef, useState } from 'react'
import {
  getAutoScrollSpeed,
  getIntersectingItemIds,
  hasExceededDragThreshold,
  haveSameSelection,
  mergeUniqueSelection
} from 'utils/fileMultiSelection'

const INTERACTIVE_SELECTOR = 'button, input, a, [role="dialog"]'
const CLICK_AWAY_IGNORE_SELECTOR = '.file-card, button, input, a, [role="button"], [role="dialog"], .MuiModal-root'

const rectFromPoints = (start, end) => ({
  left: Math.min(start.x, end.x),
  top: Math.min(start.y, end.y),
  width: Math.abs(end.x - start.x),
  height: Math.abs(end.y - start.y)
})

const useMarqueeSelection = ({ containerRef, selectedIds, onChange, disabled = false }) => {
  const [isDragSelecting, setIsDragSelecting] = useState(false)
  const [selectionRect, setSelectionRect] = useState(null)

  const selectedIdsRef = useRef(selectedIds)
  const isMouseDownRef = useRef(false)
  const isDragSelectingRef = useRef(false)
  const dragStartRef = useRef({ x: 0, y: 0 })
  const dragSelectionBaseRef = useRef([])
  const selectedDuringDragRef = useRef(new Set())
  const isAdditiveDragRef = useRef(false)
  const dragCardRectsRef = useRef(new Map())
  const lastDragPointerRef = useRef({ x: 0, y: 0 })
  const autoScrollFrameRef = useRef(null)
  const suppressNextContentClickRef = useRef(false)

  useEffect(() => {
    selectedIdsRef.current = selectedIds
  }, [selectedIds])

  const updateDragSelection = useCallback((clientX, clientY) => {
    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    const x = clientX - rect.left + container.scrollLeft
    const y = clientY - rect.top + container.scrollTop

    setSelectionRect(rectFromPoints(dragStartRef.current, { x, y }))

    const cards = container.querySelectorAll('.file-card[data-file-id]')
    const visitedIds = new Set()

    cards.forEach(card => {
      const fileId = card.dataset.fileId
      if (!fileId || visitedIds.has(fileId)) return
      visitedIds.add(fileId)

      const cardRect = card.getBoundingClientRect()
      const cardLeft = cardRect.left - rect.left + container.scrollLeft
      const cardTop = cardRect.top - rect.top + container.scrollTop
      dragCardRectsRef.current.set(fileId, {
        left: cardLeft,
        top: cardTop,
        right: cardLeft + cardRect.width,
        bottom: cardTop + cardRect.height
      })
    })

    const intersectingIds = getIntersectingItemIds(dragCardRectsRef.current, {
      left: dragStartRef.current.x,
      top: dragStartRef.current.y,
      right: x,
      bottom: y
    })
    const nextSelectedIds = mergeUniqueSelection(dragSelectionBaseRef.current, intersectingIds)
    const currentIds = Array.from(selectedDuringDragRef.current)

    if (!haveSameSelection(currentIds, nextSelectedIds)) {
      selectedDuringDragRef.current = new Set(nextSelectedIds)
      onChange(nextSelectedIds)
    }
  }, [containerRef, onChange])

  const startAutoScroll = useCallback(() => {
    if (autoScrollFrameRef.current) return

    const step = () => {
      autoScrollFrameRef.current = null
      if (!isDragSelectingRef.current) return

      const container = containerRef.current
      if (!container) return

      const { x, y } = lastDragPointerRef.current
      const rect = container.getBoundingClientRect()
      const speed = getAutoScrollSpeed(y - rect.top, rect.height, 120, 48)

      if (speed !== 0) {
        const previousScrollTop = container.scrollTop
        container.scrollBy(0, speed)

        if (container.scrollTop !== previousScrollTop) {
          updateDragSelection(x, y)
        }

        autoScrollFrameRef.current = window.requestAnimationFrame(step)
      }
    }

    autoScrollFrameRef.current = window.requestAnimationFrame(step)
  }, [containerRef, updateDragSelection])

  const handleMouseDown = useCallback(e => {
    if (disabled) return
    if (e.target.closest(INTERACTIVE_SELECTOR)) return
    if (e.button !== 0) return

    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    if (
      e.clientX < rect.left ||
      e.clientX > rect.right ||
      e.clientY < rect.top ||
      e.clientY > rect.bottom
    ) return

    const x = e.clientX - rect.left + container.scrollLeft
    const y = e.clientY - rect.top + container.scrollTop
    const isAdditiveDrag = e.ctrlKey || e.metaKey
    const selectionBase = isAdditiveDrag ? selectedIdsRef.current : []

    dragStartRef.current = { x, y }
    dragSelectionBaseRef.current = selectionBase
    isAdditiveDragRef.current = isAdditiveDrag
    selectedDuringDragRef.current = new Set(selectionBase)
    dragCardRectsRef.current = new Map()
    lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
    isMouseDownRef.current = true
    window.__dragMovement = 0
    e.preventDefault()
  }, [disabled, containerRef])

  const handleMouseMove = useCallback(e => {
    if (!isMouseDownRef.current && !isDragSelectingRef.current) return

    const container = containerRef.current
    if (!container) return

    lastDragPointerRef.current = { x: e.clientX, y: e.clientY }
    const rect = container.getBoundingClientRect()
    const current = {
      x: e.clientX - rect.left + container.scrollLeft,
      y: e.clientY - rect.top + container.scrollTop
    }
    const movement = Math.max(
      Math.abs(current.x - dragStartRef.current.x),
      Math.abs(current.y - dragStartRef.current.y)
    )
    window.__dragMovement = movement

    if (!isDragSelectingRef.current && hasExceededDragThreshold(dragStartRef.current, current)) {
      isDragSelectingRef.current = true
      selectedDuringDragRef.current = new Set(dragSelectionBaseRef.current)
      dragCardRectsRef.current = new Map()
      if (!isAdditiveDragRef.current) onChange([])
      setIsDragSelecting(true)
      window.__isDragSelecting = true
    }

    if (!isDragSelectingRef.current) return

    e.preventDefault()
    e.stopPropagation()
    updateDragSelection(e.clientX, e.clientY)
    startAutoScroll()
  }, [containerRef, onChange, updateDragSelection, startAutoScroll])

  const handleMouseUp = useCallback(e => {
    const completedDragSelection = isDragSelectingRef.current
    if (completedDragSelection) {
      e.preventDefault()
      e.stopPropagation()
      suppressNextContentClickRef.current = true
      window.setTimeout(() => {
        suppressNextContentClickRef.current = false
      }, 0)
    }
    isMouseDownRef.current = false
    isDragSelectingRef.current = false
    dragSelectionBaseRef.current = []
    isAdditiveDragRef.current = false
    dragCardRectsRef.current = new Map()
    if (autoScrollFrameRef.current) {
      window.cancelAnimationFrame(autoScrollFrameRef.current)
      autoScrollFrameRef.current = null
    }
    setIsDragSelecting(false)
    setSelectionRect(null)
    window.__isDragSelecting = false
    window.__dragMovement = 0
  }, [])

  const handleContentClick = useCallback(e => {
    if (disabled) return
    if (suppressNextContentClickRef.current) return
    if (e.target.closest(CLICK_AWAY_IGNORE_SELECTOR)) return

    onChange([])
  }, [disabled, onChange])

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      if (autoScrollFrameRef.current) {
        window.cancelAnimationFrame(autoScrollFrameRef.current)
        autoScrollFrameRef.current = null
      }
      isMouseDownRef.current = false
      isDragSelectingRef.current = false
      dragSelectionBaseRef.current = []
      isAdditiveDragRef.current = false
      dragCardRectsRef.current = new Map()
      window.__isDragSelecting = false
      window.__dragMovement = 0
    }
  }, [handleMouseMove, handleMouseUp])

  useEffect(() => {
    if (disabled) return undefined

    const handleKeyDown = e => {
      if (e.key !== 'Escape') return
      if (selectedIdsRef.current.length === 0) return
      onChange([])
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [disabled, onChange])

  return {
    isDragSelecting,
    selectionRect,
    handleMouseDown,
    handleContentClick
  }
}

export default useMarqueeSelection
